import Button from "components/Button";
import SearchIcon from "assets/images/search-icon.svg";
import RebrandSearchIcon from "assets/images/search-icon-2026.svg";
import { useFeatureSwitch } from "components/FeatureSwitch";
import { useNavigate } from "react-router-dom";
import { ExclamationCircleIcon } from "@heroicons/react/outline";
import { trackAnalyticsActionClicked } from "utils/Tracking";
import {
  TABLE_CONFIGS,
  DEFAULT_EMPTY_STATE,
  ANALYTICS_EMPTY_STATE,
} from "utils/analytics/tableConfigs";

interface EmptyTableStateProps {
  type?: string;
  error?: boolean;
  onRetry?: () => void;
  customMessage?: string;
}

const EmptyTableState = ({
  type = "default",
  error = false,
  onRetry,
  customMessage,
}: EmptyTableStateProps) => {
  const navigate = useNavigate();
  const isRebrand = useFeatureSwitch("REACT_APP_USE_REBRAND_2026");

  const getEmptyStateConfig = () => {
    // Use analytics state for analytics type
    if (type === "analytics") {
      return ANALYTICS_EMPTY_STATE;
    }

    // Look up config from table configs if type matches
    const tableConfig = type ? TABLE_CONFIGS[type] : undefined;
    if (tableConfig && typeof tableConfig === "object" && tableConfig.emptyState) {
      return tableConfig.emptyState;
    }

    // Fall back to default
    return DEFAULT_EMPTY_STATE;
  };

  // Error state rendering
  if (error) {
    const errorMessage = customMessage || "We couldn't retrieve your data. Please try again.";

    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="flex flex-col items-center text-center max-w-sm">
          <div className="w-14 h-14 rounded-full bg-accent-100 flex items-center justify-center mb-4">
            <ExclamationCircleIcon className="w-8 h-8 text-accent-600 rebrand:text-accent-800" />
          </div>

          <h3 className="text-base font-medium text-gray-900">
            Unable to load{" "}
            {type === "error"
              ? "error logs"
              : type === "domain"
              ? "domain analytics"
              : "data"}
          </h3>

          <p className="my-2 text-sm text-gray-500">
            {errorMessage}
          </p>

          {onRetry && (
            <Button
              text="Try Again"
              onClick={onRetry}
              className="button button-secondary rounded mt-4"
            />
          )}
        </div>
      </div>
    );
  }

  // Empty state rendering
  const config = getEmptyStateConfig();
  const emptyMessage = customMessage || config.message;

  return (
    <div className="flex flex-col items-center justify-center py-12 px-4">
      <div className="flex flex-col items-center text-center max-w-sm">
        <img alt="Magnifying glass and document" src={isRebrand ? RebrandSearchIcon : SearchIcon} />
        <h3 className="text-base font-medium text-gray-900">{config.title}</h3>
        <p className="my-2 mb-4 text-sm text-gray-500">{emptyMessage}</p>
        <Button
          text="Start Scraping"
          onClick={() => {
            navigate("/apiplayground?method=api")
            trackAnalyticsActionClicked({ section: 'global_all', action: 'start_scraping', target: 'api_playground' });
          }}
          className="button button-secondary rounded mt-4"
        />
      </div>
    </div>
  );
};

export default EmptyTableState;
